import React from 'react';
import Box, { BoxProps } from '@mui/material/Box';
import { useReveal } from '../hooks/useReveal';
import { tokens } from '../theme';

export interface RevealProps extends BoxProps {
  /**
   * Delay in ms before the entrance starts — use for sibling stagger
   * (design token: 70ms per sibling, e.g. delay={index * 70}).
   */
  delay?: number;
  children?: React.ReactNode;
}

/**
 * Scroll-reveal wrapper — "Warm Editorial" motion system.
 *
 * Entrance: opacity 0→1 + translateY(20px)→0, 400ms,
 * cubic-bezier(0.16, 1, 0.3, 1) (expo-out). Fires once, at 20% viewport.
 * Respects prefers-reduced-motion: renders the final state with no transition.
 *
 * Accepts all Box props (component, sx, etc.) so it can wrap any layout node.
 */
const Reveal: React.FC<RevealProps> = ({ delay = 0, children, sx, ...rest }) => {
  const { ref, revealed, reduced } = useReveal<HTMLDivElement>();

  return (
    <Box
      ref={ref}
      sx={[
        {
          opacity: revealed ? 1 : 0,
          transform: revealed ? 'none' : 'translateY(20px)',
          transition: reduced
            ? 'none'
            : `opacity ${tokens.motion.reveal}ms ${tokens.motion.easeEntrance} ${delay}ms, transform ${tokens.motion.reveal}ms ${tokens.motion.easeEntrance} ${delay}ms`,
          willChange: revealed ? 'auto' : 'opacity, transform',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default Reveal;
